/**
 * Available Footswitch Functions
 * Based on OFFICIAL Looper Plus User Guide (Version 1.0.0)
 */

export const FOOTSWITCH_FUNCTIONS = [
    // Core Pedal Functions
    { value: 'rec_play', label: 'Rec/Play/Overdub A (Cycle)', category: 'Core Controls' },
    { value: 'stop', label: 'Stop Pedal A', category: 'Core Controls' },
    { value: 'rec_play_b', label: 'Rec/Play/Overdub B (Cycle)', category: 'Core Controls' },
    { value: 'stop_b', label: 'Stop Pedal B', category: 'Core Controls' },
    { value: 'stop_all', label: 'Stop All (A + B)', category: 'Core Controls' },

    // Track Control
    { value: 'change_track_focus', label: 'Swap Focus (A ↔ B)', category: 'Track Control' },
    { value: 'select_track_a', label: 'Select Track A', category: 'Track Control' },
    { value: 'select_track_b', label: 'Select Track B', category: 'Track Control' },

    { value: 'solo_track_a', label: 'Solo Track A', category: 'Track Control' },
    { value: 'solo_track_b', label: 'Solo Track B', category: 'Track Control' },
    { value: 'solo_current_track', label: 'Solo Current Track', category: 'Track Control' },

    { value: 'mute_track_a', label: 'Mute Track A', category: 'Track Control' },
    { value: 'mute_track_b', label: 'Mute Track B', category: 'Track Control' },
    { value: 'mute_current_track', label: 'Mute Current Track', category: 'Track Control' },

    // Editing
    { value: 'undo_redo', label: 'Undo/Redo', category: 'Editing' },
    { value: 'clear', label: 'Clear Track/All', category: 'Editing' },

    // Effects
    { value: 'reverse', label: 'Reverse Toggle', category: 'Effects' },
    { value: 'speed', label: 'Speed (Normal/Half)', category: 'Effects' },
    { value: 'fade', label: 'Fade In/Out', category: 'Effects' },

    // Loop Management
    { value: 'load_next_slot', label: 'Load Next Slot', category: 'Loop Management' },
    { value: 'load_prev_slot', label: 'Load Previous Slot', category: 'Loop Management' },

    // Mode Selection
    { value: 'mode_single', label: 'Mode: Single', category: 'Looper Modes' },
    { value: 'mode_multi', label: 'Mode: Multi', category: 'Looper Modes' },
    { value: 'mode_sync', label: 'Mode: Sync', category: 'Looper Modes' },
    { value: 'mode_song', label: 'Mode: Song', category: 'Looper Modes' }
];

/**
 * Available Encoder Functions
 */
export const ENCODER_FUNCTIONS = [
    { value: 'volume', label: 'Main Volume' },
    { value: 'fade_time', label: 'Fade Time' },
    { value: 'track_a_volume', label: 'Track A Volume' },
    { value: 'track_b_volume', label: 'Track B Volume' },
    { value: 'crossfade', label: 'Crossfade A/B' },
    { value: 'speed', label: 'Playback Speed' },
    { value: 'menu_navigate', label: 'Menu Navigation' }
];

/**
 * MIDI Function to Note/CC Mapping
 * Based on OFFICIAL Sheeran Looper + User Guide v1.0.0 - Section 4.5.4 MIDI Control
 * (note numbers VERIFIED 2026-06-20 against the manual; CC/encoder values below are NOT in the manual)
 */
export const MIDI_FUNCTION_MAP = {
    // Core Pedal Functions (Note On)
    stop: { type: 'note', value: 7 },
    stop_b: { type: 'note', value: 8 },
    rec_play: { type: 'note', value: 11 },
    rec_play_b: { type: 'note', value: 12 },

    // Virtual Combo Actions
    stop_all: { type: 'combo', values: [7, 8] },

    // Effects (Note On)
    speed: { type: 'note', value: 17 },
    reverse: { type: 'note', value: 29 },
    fade: { type: 'note', value: 34 },

    // Editing (Note On)
    undo_redo: { type: 'note', value: 45 },
    clear: { type: 'note', value: 49 },

    // Loop Management (Note On)
    load_next_slot: { type: 'note', value: 65 },
    load_prev_slot: { type: 'note', value: 66 },

    // Track Control (Note On)
    solo_track_a: { type: 'note', value: 67 },
    solo_track_b: { type: 'note', value: 68 },
    solo_current_track: { type: 'note', value: 69 },

    mute_track_a: { type: 'note', value: 70 },
    mute_track_b: { type: 'note', value: 71 },
    mute_current_track: { type: 'note', value: 72 },

    // Looper Modes (Note On)
    mode_single: { type: 'note', value: 73 },
    mode_multi: { type: 'note', value: 74 },
    mode_sync: { type: 'note', value: 75 },
    mode_song: { type: 'note', value: 76 },

    // Track Focus (Note On)
    change_track_focus: { type: 'note', value: 77 }, // Swap
    select_track_a: { type: 'note', value: 78 },
    select_track_b: { type: 'note', value: 79 },

    // Encoder Functions (Control Change) - NOT DOCUMENTED IN MANUAL
    // These are assumptions based on common MIDI CC usage
    volume: { type: 'cc', value: 7 },
    track_a_volume: { type: 'cc', value: 14 },
    track_b_volume: { type: 'cc', value: 15 },
    fade_time: { type: 'cc', value: 16 },
    crossfade: { type: 'cc', value: 17 },
    speed_cc: { type: 'cc', value: 18 },
    menu_navigate: { type: 'cc', value: 60 } // NOT DOCUMENTED IN MANUAL - assumption
};

/**
 * Get user-friendly label for function
 */
export function getFunctionLabel(functionValue) {
    const func = FOOTSWITCH_FUNCTIONS.find(f => f.value === functionValue);
    if (func) return func.label;

    const enc = ENCODER_FUNCTIONS.find(e => e.value === functionValue);
    if (enc) return enc.label;

    return functionValue;
}

/**
 * Get MIDI command for function
 */
export function getMIDICommand(functionValue) {
    return MIDI_FUNCTION_MAP[functionValue] || null;
}
